import React, { useContext, useState, useEffect, useCallback } from 'react';
import { companyPageContext } from '@/components/NewCompanyPage/NewCompanyPage';
import TpaTable from '@/components/CompanyConfig/TpaTable/TpaTable';
import TpaFormModal from '@/components/CompanyConfig/TpaTable/TpaFormModal';
import generalStyles from '@/components/General/Styles.module.scss';
import { noOp } from '@/components/General/HandleApiError';
import styles from './MultiCompanySettingsTab.module.scss';
import API_STATES from '@/constants/StateConstants';
import { getTpas, setCompanyTpaType } from '@/services/api/company.service';

const TPA_TYPE_OPTIONS = [
    { value: 'niq_partner', label: 'NIQ Partner' },
    { value: 'broker', label: 'Broker' },
    { value: 'consultant', label: 'Consultant' },
    { value: 'other', label: 'Other' },
];

function getTpaTypeLabel(value) {
    return TPA_TYPE_OPTIONS.find((opt) => opt.value === value)?.label ?? 'None';
}

export default function MultiCompanySettingsTab({ onChange = noOp }) {
    const { nsCompanyId, companyConfig } = useContext(companyPageContext);
    const companyId = companyConfig?.companyId;
    const isMultiCompany = companyConfig?.isMultiCompany;
    const companyName = companyConfig?.companyName;
    const companyAccessLevel = companyConfig?.companyAccessLevel;


    const [tpaType, setTpaType] = useState('');
    const [pendingTpaType, setPendingTpaType] = useState('');
    const [isEditingTpaType, setIsEditingTpaType] = useState(false);
    const [tpaTypeApiState, setTpaTypeApiState] = useState(API_STATES.none);
    const [tpaTypeApiError, setTpaTypeApiError] = useState('');

    const [tpas, setTpas] = useState([]);
    const [tpasApiState, setTpasApiState] = useState(API_STATES.none);

    const [formModalOpen, setFormModalOpen] = useState(false);
    const [selectedTpa, setSelectedTpa] = useState(null);

    useEffect(() => {
        setTpaType(companyConfig?.tpaType ?? '');
    }, [companyConfig?.tpaType]);

    const loadTpas = useCallback(async () => {
        if (!companyId) return;
        setTpasApiState((prev) => (prev === API_STATES.success ? API_STATES.refreshing : API_STATES.loading));
        const response = await getTpas(companyId);
        if (response.status === API_STATES.success) {
            setTpas(response.data);
            setTpasApiState(API_STATES.success);
        } else {
            setTpas([]);
            setTpasApiState(API_STATES.error);
        }
    }, [companyId]);

    useEffect(() => {
        loadTpas();
    }, [loadTpas]);

    function openTpaTypeEdit() {
        setPendingTpaType(tpaType);
        setTpaTypeApiError('');
        setIsEditingTpaType(true);
    }

    function cancelTpaTypeEdit() {
        setIsEditingTpaType(false);
        setTpaTypeApiState(API_STATES.none);
        setTpaTypeApiError('');
    }

    async function handleSaveTpaType() {
        setTpaTypeApiState(API_STATES.loading);
        const response = await setCompanyTpaType(nsCompanyId, pendingTpaType || null);
        if (response.status === API_STATES.success) {
            setTpaType(pendingTpaType);
            setIsEditingTpaType(false);
            setTpaTypeApiState(API_STATES.none);
            onChange();
        } else {
            setTpaTypeApiState(API_STATES.error);
            setTpaTypeApiError('Failed to save TPA type. Please try again.');
        }
    }

    function openAddModal() {
        setSelectedTpa(null);
        setFormModalOpen(true);
    }

    function openEditModal(tpa) {
        setSelectedTpa(tpa);
        setFormModalOpen(true);
    }

    function handleFormSuccess() {
        setFormModalOpen(false);
        setSelectedTpa(null);
        loadTpas();
    }

    return (
        <div className={styles.container}>
          <div>
              <h2 style={{ color: 'black' }}>
                  {companyName}
                  {isMultiCompany && <span className={generalStyles.statusMessage}>(multi company)</span>}
              </h2>
              <strong>{companyAccessLevel}</strong>
          </div>
          <br />
            <div className={styles.tableHeader}>
                <div className={styles.tpaTypeRow}>
                    <span className={styles.tpaTypeLabel}>TPA Type:</span>
                    {isEditingTpaType ? (
                        <>
                            <select
                                value={pendingTpaType}
                                onChange={(e) => setPendingTpaType(e.target.value)}
                                disabled={tpaTypeApiState === API_STATES.loading}
                                className={styles.tpaTypeSelect}
                            >
                                {TPA_TYPE_OPTIONS.map((opt) => (
                                    <option key={opt.value} value={opt.value}>
                                        {opt.label}
                                    </option>
                                ))}
                            </select>
                            <button
                                className={styles.addTpaButton}
                                onClick={handleSaveTpaType}
                                disabled={tpaTypeApiState === API_STATES.loading}
                            >
                                {tpaTypeApiState === API_STATES.loading ? 'Saving...' : 'Save'}
                            </button>
                            <button
                                className={generalStyles.submitButton}
                                onClick={cancelTpaTypeEdit}
                                disabled={tpaTypeApiState === API_STATES.loading}
                            >
                                Cancel
                            </button>
                            {tpaTypeApiState === API_STATES.error && (
                                <span className={generalStyles.errorMessage}>{tpaTypeApiError}</span>
                            )}
                        </>
                    ) : (
                        <>
                            <strong className={styles.tpaTypeValue}>{getTpaTypeLabel(tpaType)}</strong>
                            <button className={generalStyles.smallButton} onClick={openTpaTypeEdit}>
                                Edit
                            </button>
                        </>
                    )}
                </div>
                <button className={styles.addTpaButton} onClick={openAddModal} disabled={!isMultiCompany}>
                    + Add New TPA
                </button>
            </div>

            {tpasApiState === API_STATES.loading && (
                <span className={generalStyles.statusMessage}>Loading TPA records...</span>
            )}
            {tpasApiState === API_STATES.error && (
                <span className={generalStyles.errorMessage}>Failed to load TPA records. Please try again.</span>
            )}
            {(tpasApiState === API_STATES.success || tpasApiState === API_STATES.refreshing) &&
                (tpas.length === 0 ? (
                    <p className={generalStyles.statusMessage}>
                        {isMultiCompany
                            ? 'No TPAs have been added to this Multi Company Account yet. Click the + Add New TPA button at the top to get started.'
                            : 'Multi Company Login is not enabled for this account. To grant Multi Company access, enable the toggle on the General tab.'}
                    </p>
                ) : (
                    <TpaTable data={tpas} companyId={companyId} onEdit={openEditModal} onRefresh={loadTpas} />
                ))}

            <TpaFormModal
                show={formModalOpen}
                onClose={() => {
                    setFormModalOpen(false);
                    setSelectedTpa(null);
                }}
                onSuccess={handleFormSuccess}
                companyId={companyId}
                tpa={selectedTpa}
            />
        </div>
    );
}
